import React, { useState } from 'react';
import {
  Alert, Button, Modal, Form, Input, Radio, Space, Tag, Typography,
  Descriptions, Popconfirm, message, Tooltip, Collapse, Table,
} from 'antd';
import {
  RollbackOutlined, UnlockOutlined, ClockCircleOutlined,
  EditOutlined, HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { requestReconcile, cancelReconcile } from '../api';

const { Text, Paragraph } = Typography;

const TARGET_LABELS = {
  GOLD_RECEIPT: 'Gold Receipt / valuation',
  CASE_DETAILS: 'Case details',
};

const TARGET_HINTS = {
  GOLD_RECEIPT: 'Weights, purity, final rate, processing fee and rounding on the gold receipt.',
  CASE_DETAILS: 'Lender, loan account, closure figures, estimated gold items and bank details.',
};

const fmtDateTime = (d) => (d ? dayjs(d).format('DD MMM YYYY, hh:mm A') : '—');

/* ══════════════════════════════════════════════════════════
   Request modal
   ══════════════════════════════════════════════════════════ */

const ReconcileRequestModal = ({ open, caseId, targets = [], onClose, onSubmitted }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const eligibleTargets = targets.filter((t) => t.eligible);
  const blockedTargets = targets.filter((t) => !t.eligible);

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestReconcile(caseId, values);
      messageApi.success(res.message || 'Reconciliation submitted for approval');
      if (res.workflowWarning) messageApi.warning(res.workflowWarning, 8);
      form.resetFields();
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to submit reconciliation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={<span><RollbackOutlined /> &nbsp;Request Reconciliation</span>}
        open={open}
        onCancel={() => { form.resetFields(); onClose?.(); }}
        onOk={handleOk}
        okText="Submit for Approval"
        okButtonProps={{ loading: submitting, disabled: eligibleTargets.length === 0 }}
        width={620}
        destroyOnClose
      >
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          This case is locked. A reconciliation asks an approver for permission to re-open
          <strong> one </strong> part of it and correct a mistake. Once approved you get
          <strong> a single save</strong> — the case re-locks the moment you submit the correction.
        </Paragraph>

        {eligibleTargets.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message="Nothing on this case can be reconciled right now."
            description={
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {blockedTargets.map((t) => (
                  <li key={t.target}><strong>{t.label}:</strong> {t.reason}</li>
                ))}
              </ul>
            }
          />
        ) : (
          <Form form={form} layout="vertical" requiredMark>
            <Form.Item
              name="target"
              label="What needs correcting?"
              rules={[{ required: true, message: 'Choose what to re-open' }]}
              initialValue={eligibleTargets[0]?.target}
            >
              <Radio.Group style={{ width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {eligibleTargets.map((t) => (
                    <Radio key={t.target} value={t.target} style={{ alignItems: 'flex-start' }}>
                      <div>
                        <Text strong>{t.label}</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{TARGET_HINTS[t.target]}</Text></div>
                      </div>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>

            {blockedTargets.length > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Not available on this case"
                description={
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {blockedTargets.map((t) => (
                      <li key={t.target}><strong>{t.label}:</strong> {t.reason}</li>
                    ))}
                  </ul>
                }
              />
            )}

            <Form.Item
              name="reason"
              label="What is wrong, and what will you change it to?"
              rules={[
                { required: true, message: 'A reason is required' },
                { min: 10, message: 'Please give the approver enough detail (at least 10 characters)' },
              ]}
              extra="The approver sees only this — be specific about the wrong value and the correct one."
            >
              <Input.TextArea
                rows={4}
                placeholder="e.g. Gross weight of item 2 was entered as 45.60 gm but the physical weighing slip shows 4.560 gm."
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

/* ══════════════════════════════════════════════════════════
   Header button — raise a request
   ══════════════════════════════════════════════════════════ */

export const ReconcileButton = ({ caseId, reconcileState, canReconcile, onChanged }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const targets = reconcileState?.targets || [];
  const status = reconcileState?.reconciliation?.status || 'NONE';

  if (!canReconcile) return null;

  // While one is in flight the banner owns the interaction, not this button.
  if (status === 'PENDING' || status === 'UNLOCKED') return null;

  const anyEligible = targets.some((t) => t.eligible);
  if (!anyEligible) return null;

  return (
    <>
      <Tooltip title="Ask for approval to re-open this case and correct a mistake">
        <Button
          size="small"
          icon={<RollbackOutlined />}
          onClick={() => setModalOpen(true)}
          style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
        >
          Reconcile
        </Button>
      </Tooltip>
      <ReconcileRequestModal
        open={modalOpen}
        caseId={caseId}
        targets={targets}
        onClose={() => setModalOpen(false)}
        onSubmitted={onChanged}
      />
    </>
  );
};

/* ══════════════════════════════════════════════════════════
   Banner — pending / unlocked state
   ══════════════════════════════════════════════════════════ */

export const ReconcileBanner = ({ caseId, reconcileState, canReconcile, onChanged, onEditTarget }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [cancelling, setCancelling] = useState(false);

  const recon = reconcileState?.reconciliation;
  const status = recon?.status || 'NONE';
  if (status !== 'PENDING' && status !== 'UNLOCKED') return null;

  const label = TARGET_LABELS[recon.target] || 'this case';

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelReconcile(caseId);
      messageApi.success('Reconciliation cancelled');
      onChanged?.();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to cancel reconciliation');
    } finally {
      setCancelling(false);
    }
  };

  const detail = (
    <Descriptions size="small" column={{ xs: 1, sm: 2 }} style={{ marginTop: 8 }}>
      <Descriptions.Item label="Re-opening">{label}</Descriptions.Item>
      <Descriptions.Item label="Requested">{fmtDateTime(recon.requestedAt)}</Descriptions.Item>
      <Descriptions.Item label="Reason" span={2}>{recon.reason || '—'}</Descriptions.Item>
      {status === 'UNLOCKED' && (
        <Descriptions.Item label="Approved">{fmtDateTime(recon.approvedAt)}</Descriptions.Item>
      )}
    </Descriptions>
  );

  return (
    <>
      {contextHolder}
      {status === 'PENDING' ? (
        <Alert
          type="warning"
          showIcon
          icon={<ClockCircleOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <Space>
              <Text strong>Reconciliation awaiting approval</Text>
              <Tag color="orange">{label}</Tag>
            </Space>
          }
          description={
            <>
              <Text type="secondary">
                The case stays locked until an approver decides. Track it under Workflow → My Approvals.
              </Text>
              {detail}
            </>
          }
          action={
            canReconcile ? (
              <Popconfirm
                title="Withdraw this reconciliation?"
                description="The request is cancelled and the case stays as it is."
                onConfirm={handleCancel}
                okText="Withdraw"
                cancelText="Keep it"
              >
                <Button size="small" danger loading={cancelling}>Withdraw</Button>
              </Popconfirm>
            ) : null
          }
        />
      ) : (
        <Alert
          type="success"
          showIcon
          icon={<UnlockOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <Space>
              <Text strong>Approved — unlocked to correct the {label.toLowerCase()}</Text>
              <Tag color="green">ONE SAVE</Tag>
            </Space>
          }
          description={
            <>
              <Text type="secondary">
                Make the correction now. The unlock is spent the moment you save, and the case
                re-locks itself — a further change needs a new reconciliation.
              </Text>
              {detail}
            </>
          }
          action={
            <Space direction="vertical">
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => onEditTarget?.(recon.target)}
              >
                {recon.target === 'GOLD_RECEIPT' ? 'Edit Gold Receipt' : 'Edit Case'}
              </Button>
              {canReconcile && (
                <Popconfirm
                  title="Give up this unlock?"
                  description="The case re-locks immediately and a new approval will be needed."
                  onConfirm={handleCancel}
                  okText="Give it up"
                  cancelText="Keep it"
                >
                  <Button size="small" danger loading={cancelling}>Cancel</Button>
                </Popconfirm>
              )}
            </Space>
          }
        />
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════
   History — every past reconciliation on this case
   ══════════════════════════════════════════════════════════ */

export const ReconcileHistory = ({ reconcileState }) => {
  const history = reconcileState?.history || [];
  if (history.length === 0) return null;

  const STATUS_COLORS = {
    UNLOCKED: 'green', REJECTED: 'red', CANCELLED: 'default', PENDING: 'orange',
  };

  return (
    <Collapse
      ghost
      style={{ marginBottom: 16 }}
      items={[{
        key: 'history',
        label: (
          <Space>
            <HistoryOutlined />
            <Text strong>Reconciliation history</Text>
            <Tag>{history.length}</Tag>
          </Space>
        ),
        children: (
          <Table
            size="small"
            bordered
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={history.map((h, i) => ({ ...h, key: i }))}
            columns={[
              {
                title: 'Outcome',
                dataIndex: 'status',
                render: (v, r) => (
                  <Tag color={STATUS_COLORS[v] || 'default'}>
                    {r.consumedAt ? 'CORRECTED' : v}
                  </Tag>
                ),
              },
              {
                title: 'Re-opened',
                dataIndex: 'target',
                render: (v) => TARGET_LABELS[v] || v || '—',
              },
              { title: 'Reason', dataIndex: 'reason', render: (v) => v || '—' },
              { title: 'Requested', dataIndex: 'requestedAt', render: fmtDateTime },
              {
                title: 'Closed',
                render: (_, r) => fmtDateTime(r.consumedAt || r.rejectedAt || r.cancelledAt),
              },
              {
                title: 'Approver note',
                dataIndex: 'rejectionReason',
                render: (v) => v || '—',
              },
            ]}
          />
        ),
      }]}
    />
  );
};

export default ReconcileRequestModal;
